// SEO-12 · /resources/how-to-reconcile-school-fee-collection
//
// EDITORIAL GROUNDING (see docs/marketing/RESOURCES_EDITORIAL_PROCESS.md):
//   • BYOG collection, invoice-bound orders, webhook-confirmed status — CLAUDE.md "Payments
//     subsystem" + "Collection is fully consolidated onto BYOG payments_v2"; claims registry X1/I1.
//   • Sequential receipt numbers from one per-tenant register + DUPLICATE band on reprints — P4
//     "FR-001 sequential receipts".
//   • UPI-to-VPA claims (server-derived amount, staff approval, duplicate-UTR guard, maker-checker)
//     — core/upi_collect.py.
//   • Refund pinned to the originating provider — fee-audit Batch 2.
//   • Integer paise end-to-end — CLAUDE.md "Domain rules".
//   • Period close returns 423 for a back-dated write — core/period_close.py.
//   • Bank reconciliation matches a reference/UTR token in the narration, then amount + date —
//     core/bank_statement.py + finance_ai.reconcile_bank.
//   • GST is back-computed from a GST-INCLUSIVE amount per fee head (HSN/SAC) — core/fee_gst.py.
// NO invented statistics, recovery rates, benchmark figures or customer outcomes.

const article = {
  slug: "how-to-reconcile-school-fee-collection",
  title: "How to reconcile a month of school fee collection",
  seoTitle: "How to Reconcile School Fee Collection | Vidyapeeth360",
  description:
    "A month-end fee reconciliation method for Indian schools taking cash, cheque, bank transfer, UPI and gateway payments — and what to do with the residue.",
  topic: "Fees & finance",
  topicKey: "fees",
  readingMinutes: 6,
  published: "2026-08-18",
  updated: "2026-08-18",
  standfirst:
    "Most Indian schools take fees on five or six different rails at once. Reconciliation is not an accounting chore at the end of that — it is the only way to know which of those rails is quietly losing money.",
  moduleLinks: ["fee-collection", "receipt-generation", "refund-management", "accounting-reports"],
  conversion: {
    to: "/pricing",
    label: "See plans and the full cost schedule",
    note: "Subscription, gateway charges and per-message rates are published, not quoted on request.",
  },
  sections: [
    {
      id: "why-it-drifts",
      heading: "Why school fee money drifts",
      blocks: [
        {
          t: "p",
          text: "A single term's fees arrive as counter cash, a cheque that clears three days later, a NEFT into the school's bank account, a UPI transfer to the school's VPA, and an online gateway payment. Each produces a different record, at a different moment, held by a different person. The office ledger says one thing on the 5th, the bank statement another on the 8th, the gateway a third on the 10th.",
        },
        {
          t: "p",
          text: "Nothing here is fraud. It is timing and attribution. But if nobody forces those three views to agree, the gap becomes permanent — and by March nobody can say whether a family owes ₹18,400 or ₹4,000. The method below works whether you run it in a spreadsheet or in software.",
        },
      ],
    },
    {
      id: "principles",
      heading: "Six rules that make reconciliation possible at all",
      blocks: [
        {
          t: "p",
          text: "Reconciliation is decided before month-end, by how money is allowed to be recorded at all. Six rules do most of the work.",
        },
        {
          t: "ol",
          items: [
            "**Every rupee attaches to an invoice.** A payment with no invoice is an unallocated credit, and that is where reconciliation goes to die. An overpayment should become a named credit on the student's ledger, not a note in the margin.",
            "**Receipt numbers come from one register, in sequence.** One school, one running series, no gaps, no per-counter improvisation. A reprint must be visibly marked as a duplicate, so a family cannot present the same receipt twice as two payments.",
            "**A payment is confirmed by the provider, not by a closed browser tab.** A parent whose phone died mid-payment has not necessarily failed to pay. The invoice should move to paid when the gateway account confirms the capture, and stay pending until then.",
            "**UPI into the school's VPA needs a human check.** A screenshot and a UTR are a claim, not a payment. Someone other than the person who submitted it should verify the credit, and the same UTR must never be accepted twice.",
            "**Refunds go back the way the money came.** A refund on a gateway payment belongs on that gateway, against the original order — not as a cash payout that will never match anything.",
            "**Amounts are stored as integers.** ₹12,450 is 12,45,000 paise, not 12450.00 held as a floating-point number. Once a system stores fractions of a rupee, every total is approximately right and nothing ever ties.",
          ],
        },
      ],
    },
    {
      id: "the-sequence",
      heading: "The month-end sequence",
      blocks: [
        {
          t: "p",
          text: "Run these in order — each step assumes the one before it is finished, which is what stops you chasing a difference twice.",
        },
        {
          t: "ol",
          items: [
            "**Stop the month.** Decide the cut-off date and close the books through it first. A period that stays open while you reconcile is one where a colleague can back-date a receipt into a total you already agreed.",
            "**Bank statement against the ledger.** Match the bank's own statement to your recorded receipts, first on a reference the narration actually carries — a UTR or a gateway payment reference. That match is exact and needs no date window. Only then fall back to amount and a few days' proximity.",
            "**Clear the manual-claim queue.** Every pending UPI or bank-transfer claim is either a credit you have not receipted or one you should reject. Leaving it pending pushes the difference into next month.",
            "**Reconcile the gateway separately from the bank.** A gateway shows captured payments; the bank shows settled deposits, net of charges. Both are correct. Reconcile captures to invoices and settlements to bank credits — never expect one figure twice.",
            "**Reconcile refunds.** Every refund should have an approval, a provider-confirmed status and a matching debit. A refund approved in your records but never confirmed by the provider is not a refund; it is an outstanding instruction.",
            "**Tie the fee register to the income heads.** The month's collection should agree with what the accounts show as fee income, split by head. Separate heads in the fee structure should be separate lines in the income statement.",
            "**Check tax per head, not per invoice.** The amount printed on an Indian school's fee notice is what the parent pays — GST, where it applies, sits inside that number rather than on top. Each head needs its own HSN/SAC and rate, with the split back-computed from the inclusive amount. Confirm your school's position with your own tax adviser.",
          ],
        },
      ],
    },
    {
      id: "the-residue",
      heading: "What to do with what does not tie",
      blocks: [
        {
          t: "p",
          text: "A month never reconciles to zero on the first pass. Sort the residue into four buckets — each has a different owner.",
        },
        {
          t: "ul",
          items: [
            "**Timing.** A cheque deposited on the 30th that clears on the 2nd. Nothing is wrong. Record it and move on.",
            "**Attribution.** Money in the bank you cannot tie to a student. Chase it now, while the narration and the date still mean something to somebody — this bucket becomes unsolvable after ninety days.",
            "**Charges.** The gap between what a parent paid and what reached the bank is the gateway's charge plus any tax on it. Book it as a cost, not a shortfall in collection.",
            "**Genuine error.** A wrong amount, a receipt against the wrong sibling, a concession applied twice. Correct it through a reversal that leaves both the original and the correction visible; editing the original away turns a small error into an audit finding.",
          ],
        },
        {
          t: "note",
          title: "One honest boundary",
          text: "No software can tell you when your bank will credit a settlement or what the provider deducted. Settlement timing, deductions and the final bank credit stay evidence from your gateway and your bank — reconciliation forces your records to agree with theirs, it does not replace reading them.",
        },
      ],
    },
    {
      id: "in-the-product",
      heading: "How this works in Vidyapeeth360",
      blocks: [
        {
          t: "p",
          text: "Schools on Vidyapeeth360 collect through their own Razorpay or Cashfree account, so money settles to the school's bank on that account's cycle. An online payment is bound to the invoice it was raised for, and moves to paid on evidence from that gateway account rather than on a redirect.",
        },
        {
          t: "p",
          text: "Receipt numbers come from one sequential per-school register, and any reprint carries a visible DUPLICATE band. UPI paid into the school's VPA arrives as a claim with a reference and proof, which a second person with fee-approval rights verifies before it becomes a receipted payment; the same reference cannot be claimed twice. Refunds are pinned to the provider the payment arrived on, and amounts are integer paise from invoice line to receipt PDF.",
        },
        {
          t: "p",
          text: "For the month-end pass, the accounting workspace reads the bank's own CSV, Excel or PDF statement and matches it against the bank ledger — first on a UTR or payment reference in the narration, then on amount and date proximity — keeping the run for review before you confirm it. Confirming never edits a money record. Closing the books through a date is a separate action, after which a back-dated voucher, receipt or journal is refused.",
        },
        {
          t: "p",
          text: "If you are still on a spreadsheet, start with the rules rather than the software: numbered receipts from one series, every payment attached to an invoice, and a cut-off you enforce. Those habits make a later move onto [fee collection](/modules/fee-collection) and [accounting reports](/modules/accounting-reports) a data-entry exercise, not a rescue.",
        },
      ],
    },
  ],
  sources: [],
};

export default article;
